import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getVisualization,
  resolveVisualizationPath,
} from "../../services/visualizations.service.js";
import { getAnimation } from "../../services/animation.service.js";
import VisualizationHeader from "../../components/visualizationDetail/VisualizationHeader.jsx";
import VisualizationEngagement from "../../components/visualizationDetail/VisualizationEngagement.jsx";
import VisualizationTheory from "../../components/visualizationDetail/VisualizationTheory.jsx";
import VisualizationStage from "../../components/visualizationDetail/VisualizationStage.jsx";
import VisualizationComment from "../../components/visualizationDetail/VisualizationComment.jsx";
import { getReduxValue } from "../../utils/redux.js";
import PathBar from "../../components/visualizationsPage/PathBar.jsx";

const VisualizationDetail = () => {
  const [publication, setPublication] = useState(null);
  const [animation, setAnimation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentsCount, setCommentsCount] = useState(0);
  const isLoggedIn = !!getReduxValue("auth.accessToken");
  const commentRef = useRef(null);
  const [path, setPath] = useState([]);
  const ROOT_FOLDER_ID = import.meta.env.VITE_VISUALIZATIONS_FOLDER_ID;
  const { "*": fullPath } = useParams();

  const pathParts = useMemo(() => {
    const parts = fullPath?.split("/").filter(Boolean) || [];
    return [ROOT_FOLDER_ID, ...parts]; // include root first
  }, [fullPath]);

  const publicationId = pathParts[pathParts.length - 1];

  const scrollToComments = () => {
    commentRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    let active = true;
    if (!publicationId) return;

    const fetchPath = async () => {
      try {
        const resolvedPath = await resolveVisualizationPath({
          path: pathParts,
        });
        if (!active) return;
        setPath(resolvedPath);
      } catch (err) {
        if (active) {
          setError(err.message);
          setPath([]);
        }
      }
    };

    fetchPath();

    return () => {
      active = false;
    };
  }, [publicationId, pathParts]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);

        // 1️⃣ fetch publication
        const pubRes = await getVisualization(publicationId);
        if (cancelled) return;

        setPublication(pubRes);
        setCommentsCount(pubRes.commentsCount);

        // 2️⃣ fetch animation if exists
        if (pubRes.animationId) {
          const animRes = await getAnimation(pubRes.animationId);
          if (cancelled) return;

          setAnimation(animRes);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [publicationId]);

  useEffect(() => {
    if (!publication) return;

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LearningResource",
      name: publication.title,
      description: publication.description,
      keywords: publication.tags?.join(", "),
    });

    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, [publication]);

  useEffect(() => {
    if (!publication) return;

    // Title
    document.title = `${publication.title} | VisualLab`;

    // Meta description
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.name = "description";
      document.head.appendChild(desc);
    }
    desc.content =
      publication.description || "Interactive visual explanation on VisualLab";

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${window.location.origin}/visualizations/${publicationId}`;
    const setMeta = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    setMeta("og:title", publication.title);
    setMeta(
      "og:description",
      publication.description || "Interactive visualization on VisualLab",
    );
    setMeta("og:type", "article");
    setMeta("og:url", window.location.href);
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = "index, follow";

    return () => {
      // optional cleanup for SPA navigation
      document.title = "VisualLab";
    };
  }, [publication]);

  if (loading) return <div className="viz-detail-loading">Loading...</div>;

  if (error) {
    const isLocked = error?.toLowerCase().includes("access") || error?.toLowerCase().includes("permission");
    if (isLocked) {
      return (
        <div className="viz-detail-locked">
          <div className="viz-detail-locked-card">
            <div className="viz-detail-locked-icon">🔒</div>
            <h2 className="viz-detail-locked-title">This content is locked</h2>
            <p className="viz-detail-locked-desc">
              You don't have access to this visualization. It may require a purchase or subscription.
            </p>
            {!isLoggedIn && (
              <p className="viz-detail-locked-hint">
                Already purchased? <a href="/login">Log in</a> to access it.
              </p>
            )}
          </div>
        </div>
      );
    }
    return <div className="viz-detail-error">Something went wrong: {error}</div>;
  }

  if (!publication) return null;

  return (
    <div className="viz-container">
      <PathBar folderPath={path} />
      <VisualizationHeader
        title={publication.title}
        description={publication.description}
        tags={publication.tags}
      />
      <VisualizationEngagement
        views={publication.views}
        likes={publication.likesCount}
        commentsCount={commentsCount}
        publishedAt={publication.publishedAt}
        isLoggedIn={isLoggedIn}
        publicationId={publicationId}
        scrollToComments={scrollToComments}
      />
      <VisualizationStage animation={animation} />
      <VisualizationTheory theory={publication.theory} />
      <VisualizationComment
        commentRef={commentRef}
        publicationId={publicationId}
        setCommentsCount={setCommentsCount}
        isLoggedIn={isLoggedIn}
      />
    </div>
  );
};

export default VisualizationDetail;
